import * as PIXI from "pixi.js"
import PixiScene from "../PixiScene";
import PixiCard from "./PixiCard";
import PixiUtil from "./PixiUtil";
import EventApi from "../../../../../api/EventsApi";

// const TWEEN = require('@tweenjs/tween.js');

export default class PixiHand extends PIXI.Container {

    private readonly scene: PixiScene;
    private readonly translations: {[key: string]: string};
    private readonly handHeight: number; //190
    private readonly hoverPadding = 10;
    private readonly border = { x: 6, y: 6 };
    private readonly padding = { x: 10, y: 10 };
    private readonly gap = 5;
    private readonly slider = new PIXI.Sprite(PIXI.Texture.WHITE);
    private readonly handBG = new PIXI.Container();
    private readonly handMask = new PIXI.Graphics();
    private readonly colorMatrix = new PIXI.filters.ColorMatrixFilter();
    public readonly hoverScale = 1.32;

    private rectBG = new PIXI.Sprite(PIXI.Texture.WHITE);
    private cardContainer = new PIXI.Container();
    private topContainer = new PIXI.Container();
    private handBorder = new PIXI.Graphics();
    private handText = new PIXI.Text('');
    private cardMap = new Map<number, PixiCard>();
    private cardIndex: Array<number> = [];
    private cardIndexZ:Array<number> = [];
    private cardX:Array<number> = [];

    private handExtent:number = 0;
    private handWidth:number = 0;
    private lastHoverIndex:number = 0;

    private afterRightUp: boolean = false;
    private handOnTop: boolean = false;
    // private isAnimating: boolean = false;
    private isFlipping: boolean = false;

    constructor(scene: PixiScene, translations: {[key: string]: string}) {
        super();
        this.name = 'handContainer';
        this.scene = scene;
        this.handHeight = this.scene.rect!.height * 0.2;

        this.translations = translations;
        this.init();
        EventApi.addEventListener(EventApi.EDIT_MODE_OFF, (e: any) => {
            this.onHover();
        });
    }

    /**
     * Инициализация
     */
    public init() {
        if (this.scene.rect) {
            this.x = 0;
            this.y = this.handOnTop ? 0 : this.scene.rect.height - this.handHeight;// - this.border.y;
            this.handWidth = this.scene.rect.width - 2 * this.border.x - 2 * this.padding.x;

            let grdData:Array<[number, string]> = [
                [0, '#25282d'],[0.5, '#383c44'],[1, '#25282d']
            ];
            this.rectBG = PixiUtil.createLinearGradient(grdData);
            this.rectBG.scale.set((this.scene.rect.width + this.border.x) / 100, (this.handHeight + this.border.y) / 100);
            this.handBG.addChild(this.rectBG);
            this.addChild(this.handBG);
            //@ts-ignore
            this.colorMatrix.brightness(1.2, false);

            const rad = 20, borderWidth = 6;
            this.handBorder.lineStyle(borderWidth, 0xff0000);
            this.handBorder.drawRoundedRect(this.border.x, 0,
                this.scene.rect.width - 2 * this.border.x, this.handHeight - this.border.y, rad);
            this.addChild(this.handBorder);
            this.handBorder.visible = false;

            this.addChild(this.cardContainer);
            this.cardContainer.name = 'cardContainer';

            this.addChild(this.handMask);
            this.cardContainer.mask = this.handMask;
            this.handMask.clear();
            this.handMask.beginFill(0xffffff);
            const height = this.handHeight - this.border.y - 2 * this.padding.y;
            this.handMask.drawRect(this.padding.x, this.padding.y - this.scene.rect.height, this.handWidth, height + this.scene.rect.height);
            this.handMask.endFill();

            this.addChild(this.topContainer);
            let topSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
            topSprite.interactive = true;
            topSprite.alpha = 0;
            topSprite.width = this.scene.rect.width;
            topSprite.height = this.hoverPadding + 1;
            topSprite.y = this.handHeight - this.border.y - this.padding.y - topSprite.height + 1;
            this.topContainer.addChild(topSprite);

            topSprite.cursor = 'pointer';
            topSprite.on('pointerup', (e) => {
                this.scene.getSelectedCard()?.emit("pointerup");
            });

            const textStyle = new PIXI.TextStyle({
                align: 'center',
                fontFamily: 'Arial',
                fontSize: 16,
                fill: 0xcccccc,
            });

            this.handText = new PIXI.Text(this.translations.empty_hand, textStyle);
            this.handText.anchor.set(0.5, 0.5);
            this.topContainer.addChild(this.handText);
            this.handText.position.set(this.scene.rect.width / 2, this.handHeight / 2);

            this.addSlider();
            // this.addDebugHUD();
        }
    }

    /**
     * Слайдер
     */
    private addSlider(): void
    {
        const sliderSize = 8;
        this.slider.width  = sliderSize;
        this.slider.height = sliderSize;
        this.slider.position.set(this.padding.x, this.handHeight - 2 * this.border.y - sliderSize);
        this.slider.tint = this.scene.playerColor;
        this.topContainer.addChild(this.slider);
        this.slider.interactive = true;
        this.slider.buttonMode  = true;
        this.slider.visible = false;

        const clamp = (min:number, val:number, max:number) => Math.min(Math.max(val, min), max);
        const onDragSlider = (e: PIXI.InteractionEvent) => {
            this.slider.x = clamp(this.padding.x,
                this.slider.x + (e.data.originalEvent as PointerEvent).movementX,
                this.handWidth + this.padding.x - this.slider.width);
            this.updateCardContainer();
        };

        this.slider
            .on('pointerdown', () => {
                this.parent.on( 'pointermove', onDragSlider);
            })
            .on('pointerup', () => {
                this.parent.off('pointermove', onDragSlider);
            })
            .on('pointerupoutside', () => {
                this.parent.off('pointermove', onDragSlider);
            });
    }

    /**
     * Полная очистка
     */
    public clearAll(): void
    {
        this.cardMap = new Map<number, PixiCard>();
        this.cardIndex = [];
        this.cardIndexZ = [];
        this.cardX = [];
    }

    /**
     * Количество карт в руке
     */
    public get handSize(): number
    {
        return this.cardIndex.length;
    }

    /**
     * Получить слой подложки руки
     */
    public get bgLayer(): PIXI.Container
    {
        return this.handBG;
    }

    /**
     * Получить высоту руки
     */
    public getHandHeight(withPadding:boolean = false): number
    {
        return this.handHeight + (withPadding? this.padding.y: 0);
    }

    private updateCardContainer(): void
    {
        let value = (this.slider.x - this.padding.x) / (this.handWidth - this.slider.width);
        this.cardContainer.x = - value * (this.cardContainer.width - this.handWidth);
    }

    /**
     * HUD для отладки:
     */
    public addDebugHUD(): void
    {
        const width  = this.scene.rect!.width - 2 * this.border.x;
        const height = this.handHeight - this.border.y;
        const hud = new PIXI.Graphics();
        hud.lineStyle(1, 0x33ff00, 0.5);
        hud.drawRect(this.padding.x, this.padding.y, this.handWidth, height - 2 * this.padding.y);
        hud.moveTo(0, height/2); hud.lineTo(width, height/2);
        hud.moveTo(width/2, 0); hud.lineTo(width/2, height);
        this.topContainer.addChild(hud);
    }

    /**
     * Курсор над Рукой
     */
    public isPointOverHand(point: PIXI.IPointData, updTint: boolean): boolean
    {
        let contains = false;
        if (this.visible) {
            contains = (0 < point.x && point.x < this.handBG.width
                && 0 < point.y - this.y
                && point.y - this.y < this.handBG.height
            );
            if (updTint) {
                this.backgroundTint = contains;
            }
        }
        return contains;
    }

    /**
     * Курсор над левой стороной
     */
    public isPointOverLeftHalf(): boolean
    {
        let point: PIXI.IPointData = this.scene.pixiMouse.position;
        return (this.isPointOverHand(point, false) && point.x < this.handWidth / 2);
    }

    public set backgroundTint(value: boolean) {
        this.rectBG.filters = value ? [this.colorMatrix] : [];
    }

    /**
     * Масштаб карт в Руке
     */
    public getHandScale(card: PixiCard): number
    {
        let scale = (this.handHeight - 2 * this.padding.y - this.border.y) / card.size.height;
        return scale < 1 ? scale : 1;
    }

    /**
     * Координата Х в Руке
     */
    private getHandPos(card:PixiCard, i: number, selected: boolean): { x: number, y: number }
    {
        return {
            x: this.cardX[i] + card.size.width * card.handScale / 2,
            y: (this.handHeight - this.border.y) / 2 - (selected? this.hoverPadding: 0),
        };
    }

    public setHandPos(card: PixiCard): void
    {
        let pos = this.getHandPos(card, card.handIndex, false);
        card.y = pos.y;
        card.angle = 0;
    }

    /**
     * Рука: получить порядок
     */
    public getOrder(): Array<number>
    {
        return this.cardIndex;
    }

    /**
     * Рука: упорядочить
     */
    public setOrder(ids: Array<number>): void
    {
        ids.forEach((id, i) => {
            let card = this.cardMap.get(id);
            if (card) {
                card.handIndex = i;
            }
        });
        this.cardIndex = ids;
    }

    /**
     * Рука: добавление карты
     */
    public addToHand(card: any, index: number): void
    {
        card.scale.set(card.handScale);
        let id = card.cardId;
        card.handIndex = index >= 0 ? index : this.cardIndex.length;
        if (index >= 0) {
            this.cardIndex.splice(index, 0, id);
        } else {
            this.cardIndex.push(id);
        }
        this.cardMap.set(id, card);
        card.visible = true;
    }

    /**
     * Рука: удаление карты
     * @param card
     * @private
     * */
    private removeFromHand(card: PixiCard): void
    {
        const id = card.cardId;
        card.handIndex = -1;
        card.interactive = false;
        card.buttonMode  = false;

        let index = this.cardIndex.findIndex(cardId => cardId === id);
        if (index >= 0) {
            this.cardIndex.splice(index, 1);
        }
        this.cardMap.delete(id);
    }

    /**
     * Рука: удаление карты по ID
     * @param id
     * */
    public removeById(id: number): void
    {
        const card = this.cardMap.get(id);
        if (card) {
            this.removeFromHand(card);
        }
    }

    /**
     * Рука: обновить вид
     */
    public updateHand(): void
    {
        if (this.isFlipping) {
            setTimeout(() => this.updateHand(), 150);
            return;
        }
        const getCardWidth = (id:number):number => {
            const card = this.cardMap.get(id);
            return card ? (card.size.width * card.handScale) : 0;
        }

        this.slider.visible = false;
        const list = this.cardIndex;
        this.handText.visible = ! list.length;
        let extent = 0;
        if (list.length) {
            const x0 = this.padding.x;
            extent = list.reduce((result, id, i) => result + getCardWidth(id) + (i? this.gap: 0), 0);
            let curX = x0;

            if (extent > this.handWidth) {
                const extent0 = list.reduce((result, id) => result + getCardWidth(id), 0);
                let overlap = (extent0 - this.handWidth) / (list.length > 1? list.length - 1: 1);
                const minWidth = list.reduce((result, id) => Math.min(result, getCardWidth(id)), this.handWidth);
                const maxOverlap = minWidth * 2 / 3;

                if (overlap > maxOverlap) {
                    overlap = maxOverlap;
                    this.handExtent = list.reduce((result, id, i) => result + getCardWidth(id) - (i? overlap: 0), 0);
                    this.slider.width = this.handWidth * this.handWidth / this.handExtent;
                    const clamp = (min:number, val:number, max:number) => Math.min(Math.max(val, min), max);
                    this.slider.x = clamp(this.padding.x, this.slider.x, this.handWidth + this.padding.x - this.slider.width);
                    this.slider.visible = true;
                }
                this.cardX = list.map((id) => (curX += (getCardWidth(id) - overlap)));
            }
            else {
                this.cardX = list.map((id) => (curX += (getCardWidth(id) + this.gap)));
            }
            this.cardX.unshift(x0);
            // console.log('%c ### [X]', 'color: #f0f', this.cardX.map(x => ~~x));
        }

        this.cardContainer.removeChildren();

        if (list.length) {
            list.forEach((id:number, i:number) => {
                let kid = this.cardMap.get(id);
                if (kid) {
                    let pos = this.getHandPos(kid, i, kid.selected);
                    kid.scale.set(kid.handScale);
                    kid.x = pos.x;
                    kid.y = pos.y;
                    kid.handIndex = i;
                    this.cardContainer.addChild(kid);
                }
            });
        }

        if (this.slider.visible) {
            this.updateCardContainer();
        }
        else {
            this.cardContainer.x = (extent < this.handWidth) ?
                (this.scene.rect!.width - extent) / 2 - this.border.x - this.padding.x : 0;
        }

    }

    /**
     * Получить карту по id
     * @param id
     */
    public getCard(id: number): PixiCard | undefined
    {
        return this.cardMap.get(id);
    }

    /**
     * Получить карту по индексу
     * @param idx
     */
    public getCardByIdx(idx: number): PixiCard | undefined
    {
        return this.cardMap.get(this.cardIndex[idx]);
    }

    /**
     * Индекс след. карты
     * @param idx
     */
    public getPrevIdx(idx: number): number
    {
        const newIdx = idx > 0 ? idx - 1 : 0;
        if (newIdx !== idx) {
            this.updateSlider(newIdx);
        }
        return newIdx;
    }

    /**
     * Индекс пред. карты
     * @param idx
     */
    public getNextIdx(idx: number): number
    {
        const newIdx = (idx + 1 >= this.cardIndex.length) ? this.cardIndex.length : idx + 1;
        if (newIdx !== idx) {
            this.updateSlider(newIdx);
        }
        return newIdx;
    }

    /**
     * Слайдер следует за зумом
     * @param idx
     */
    public updateSlider(idx: number): void
    {
        this.onHover(this.getCardByIdx(idx));
        idx = Math.min(Math.max(0, idx), this.cardIndex.length);
        if (this.slider.visible && this.cardIndex.length) {
            const value = idx / this.cardIndex.length;
            this.cardContainer.x = - value * (this.cardContainer.width - this.handWidth);
            this.slider.x = value * (this.handWidth - this.slider.width) + this.padding.x;
        }
    }

    /**
     * "Лесенка"
     */
    public onHover(card?: PixiCard, onRightUp?: boolean): void
    {
        if (this.isFlipping) {
            return;
        }
        if (this.afterRightUp) {
            this.afterRightUp = false;
            return;
        }
        this.afterRightUp = !!onRightUp;

        let handIndex = (card && card.handIndex >= 0) ? card.handIndex : this.lastHoverIndex;
        this.lastHoverIndex = handIndex;

        this.cardIndexZ = [];
        if (this.cardIndex.length) {
            handIndex = (handIndex < this.cardIndex.length) ? handIndex : 0;
            this.cardIndexZ.unshift(handIndex);

            for (let k = 0, i = 1, n = this.cardIndex.length; i < n; ++i) {
                k = handIndex - i;
                if (k >= 0) this.cardIndexZ.unshift(k);
                k = handIndex + i;
                if (k <  n) this.cardIndexZ.unshift(k);
            }
        }

        this.updateHover();
        if (card) {
            this.updateHoverCard(card.handIndex);
        }
        this.commitNewState();
    }

    /**
     * Hand: update с Hover-ом
     */
    private updateHover(): void
    {
        this.handText.visible = ! this.cardIndex.length;
        this.cardContainer.removeChildren();
        if (this.cardIndexZ.length) {
            this.cardIndexZ.forEach(k => {
                let card = this.cardMap.get(this.cardIndex[k]);
                if (card) {
                    let pos = this.getHandPos(card, card.handIndex, card.selected);
                    card.newState.scale = card.handScale;
                    card.newState.x = pos.x;
                    card.newState.y = pos.y;
                    this.cardContainer.addChild(card);
                    card.filters = card.selected ? [ this.scene.outlineFilter ] : [];
                }
            });
        }
    }

    /**
     * Карта "подпрыгивает"
     */
    private updateHoverCard(hoverIndex: number): void
    {
        if (this.cardIndexZ.length) {
            let card = this.cardMap.get(this.cardIndex[hoverIndex]);
            if (card) {
                let cardWidth  = card.size.width  * card.handScale;
                let cardHeight = card.size.height * card.handScale;
                let shiftX = cardWidth * (this.hoverScale - 1) / 2 - 1;
                let shiftY = cardHeight * (this.hoverScale - 1) / 2;
                shiftY += (card.selected ? 0 : this.hoverPadding);

                card.newState.scale = card.handScale * this.hoverScale;
                card.newState.y -= shiftY;

                card.filters = card.selected ?
                    [ this.scene.outlineFilter, this.scene.dropShadowFilter ] :
                    [ this.scene.dropShadowFilter ];

                const list = Array.from(this.cardMap.values());
                list.forEach(card => {
                    card.newState.x += (card.handIndex < hoverIndex ? -shiftX : card.handIndex > hoverIndex ? shiftX : 0);
                });
            }
        }
    }

    /**
     * Перейти в новое состояние
     */
    private commitNewState(): void
    {
        /*function commitState(card: PixiCard) {
            card.scale.set(card.newState.scale);
            card.x = card.newState.x;
            card.y = card.newState.y;
        }*/

        const commitStateAnim = (card: PixiCard) => {
            card.tweener.addTween(card.newState, 150);
        };

        const list = Array.from(this.cardMap.values());
        if (list.length) {
            list.forEach(card => {
                commitStateAnim(card);
            });
        }
    }

    /**
     * Статус анимации поворота в руке
     */
    public set flipping(value: boolean) {
        this.isFlipping = value;
    }

    /**
     * Список id карт в руке
     */
    public getCardIds(): Array<number> {
        return this.cardIndex;
    }

    /**
     * Переместить карту
     */
    public relocate(list: Array<number>, destId: number): void
    {
        list.forEach(cardId => {
            if (cardId !== destId) {
                let cardPos = this.cardIndex.findIndex(id => id === cardId);
                if (cardPos >= 0) {
                    let tmp = this.cardIndex.splice(cardPos, 1);
                    if (destId < 0) {
                        this.cardIndex.push(...tmp);
                    }
                    else {
                        let destPos = this.cardIndex.findIndex(id => id === destId);
                        if (destPos >= 0) {
                            this.cardIndex.splice(destPos, 0, ...tmp);
                        }
                    }
                }
            }
        });
        this.updateHand();
    }

    /**
     * Показать рамку для Свинтуса
     */
    public showHandBorder(value: boolean): void
    {
        this.handBorder.visible = value;
    }

    /**
     * Управление слайдером
     */
    public handleArrows(keyValue: number): void
    {
        let value = (this.slider.x - this.padding.x) / (this.handWidth - this.slider.width);
        if (keyValue === -1) {
            value = Math.max(0, value - this.slider.width / this.handWidth);
        }
        else if (keyValue === 1) {
            value = Math.min(1, value + this.slider.width / this.handWidth);
        }
        else if (keyValue === -2) {
            value = 0;
        }
        else {
            value = 1;
        }
        this.cardContainer.x = - value * (this.cardContainer.width - this.handWidth);
        this.slider.x = value * (this.handWidth - this.slider.width) + this.padding.x;
    }

    /**
     * Показать карты
     */
    public showCards(): void
    {
        for (const card of this.cardContainer.children) {
            card.visible = true;
        }
    }

    /**
     * Отладка
     */
    public info(text?: string): void
    {
        // console.log('%c ### card Index:', 'color: #f0f', this.cardIndex);
        // console.log('%c ###   card Map:', 'color: #f0f', this.cardMap.keys());
        // this.handText.text = text ?? '';
    }

}
